import { useEffect, useState } from "react";
import { listClientViewSettingsAuditLog } from "../../api/clientSettingsApi";
import { formatDateTime } from "../midpoint/admin/users/adminUserFormatters";
import { ClientPortalError, ClientPortalLoading } from "./ClientPortalStates";

const AUDIT_LABELS = {
  "iam.user.invited": "User invited",
  "iam.user.status_changed": "User status changed",
  "iam.role.created": "Role created",
  "iam.role.updated": "Role updated",
  "iam.role.deleted": "Role deleted",
  "iam.org.assigned": "Organization assigned",
  "iam.org.removed": "Organization removed",
  "iam.assignment.updated": "Assignment updated",
};

function formatAuditEvent(type) {
  return AUDIT_LABELS[type] || type?.replace(/\./g, " › ") || "Event";
}

export default function ClientViewAuditTimeline({ clientViewId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");
    listClientViewSettingsAuditLog(clientViewId, { pageSize: 100 })
      .then((body) => {
        if (!cancelled) setEntries(body.entries || []);
      })
      .catch((error) => {
        if (!cancelled) setErrorMessage(error.message || "Unable to load audit log.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  if (loading) {
    return <ClientPortalLoading message="Loading audit log…" />;
  }

  if (errorMessage) {
    return <ClientPortalError message={errorMessage} />;
  }

  if (!entries.length) {
    return (
      <section className="panel-card cp-settings-audit">
        <p className="cp-settings-empty">No audit events recorded for this Client View yet.</p>
      </section>
    );
  }

  return (
    <section className="panel-card cp-settings-audit">
      <div className="cp-settings-audit__timeline">
        {entries.map((entry) => (
          <article key={entry.auditLogId} className="cp-settings-audit__item">
            <div className="cp-settings-audit__dot" aria-hidden />
            <div className="cp-settings-audit__body">
              <div className="cp-settings-audit__top">
                <span className="cp-settings-audit__event">{formatAuditEvent(entry.eventType)}</span>
                <time className="cp-settings-audit__time">{formatDateTime(entry.createdAt)}</time>
              </div>
              {(entry.entityType || entry.entityId) && (
                <p className="cp-settings-audit__meta">
                  {entry.entityType || "Entity"}
                  {entry.entityId ? ` · ${entry.entityId}` : ""}
                </p>
              )}
              {entry.metadata ? (
                <pre className="cp-settings-audit__detail">
                  {JSON.stringify(entry.metadata, null, 2)}
                </pre>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
